import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const SUB_TONE: Record<string, string> = {
  trial: "bg-blue-100 text-blue-700",
  active: "bg-green-100 text-green-700",
  expired: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700",
  inactive: "bg-gray-100 text-gray-700",
};

const fmt = (n: number) => (n ?? 0).toLocaleString("en-US");

// Draw the eye to accounts about to hit their ceiling.
const usageColor = (percent: number) => {
  if (percent >= 100) return "text-red-700 font-medium";
  if (percent >= 90) return "text-orange-700";
  if (percent >= 80) return "text-amber-700";
  return "";
};

const AdminUsers: React.FC = () => {
  const [query, setQuery] = useState("");
  const [emailVerified, setEmailVerified] = useState<"all" | "true" | "false">(
    "all"
  );
  const [status, setStatus] = useState<"all" | "pending" | "suspended">("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const headers = useMemo(() => {
    const token = localStorage.getItem("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query) params.set("search", query);
      if (emailVerified !== "all") params.set("emailVerified", emailVerified);
      if (status !== "all") params.set("status", status);
      const res = await fetch(
        `${API_BASE_URL}/api/admin/users?${params.toString()}`,
        { headers: headers as any }
      );
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, limit]);

  const updateStatus = async (userId: string, updates: any) => {
    await fetch(`${API_BASE_URL}/api/admin/users/${userId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...(headers as any) },
      body: JSON.stringify(updates),
    });
    await load();
  };

  const updateVerification = async (userId: string, payload: any) => {
    await fetch(`${API_BASE_URL}/api/admin/users/${userId}/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(headers as any) },
      body: JSON.stringify(payload),
    });
    await load();
  };

  const deleteUser = async (userId: string, label: string) => {
    const ok = window.confirm(
      `Delete ${label} and ALL their data?\n\nThis runs the GDPR deletion: pages, conversations, messages, comments, posts, customer profiles. Analytics are kept for 30 days then purged.\n\nThis cannot be undone.`
    );
    if (!ok) return;
    const typed = window.prompt('Type DELETE to confirm:');
    if (typed !== 'DELETE') return;
    const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", ...(headers as any) },
      body: JSON.stringify({ confirm: 'DELETE' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(`Delete failed: ${data.error || res.status}${data.details ? ` — ${data.details}` : ''}`);
      return;
    }
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Users</h2>
        <button
          className="px-3 py-1 rounded bg-red-600 text-white"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      <div className="flex gap-2 items-end">
        <div>
          <label className="text-xs text-gray-500">Search</label>
          <input
            className="border rounded px-2 py-1 w-64"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="name, email, phone"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Email verified</label>
          <select
            className="border rounded px-2 py-1"
            value={emailVerified}
            onChange={(e) => setEmailVerified(e.target.value as any)}
          >
            <option value="all">All</option>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Status</label>
          <select
            className="border rounded px-2 py-1"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <button
          className="px-3 py-2 bg-gray-900 text-white rounded"
          onClick={() => {
            setPage(1);
            load();
          }}
          disabled={loading}
        >
          Filter
        </button>
      </div>

      <div className="bg-white border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="p-2">Name</th>
              <th className="p-2">Email</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Plan</th>
              <th className="p-2">Subscription</th>
              <th className="p-2">Comments</th>
              <th className="p-2">Pages</th>
              <th className="p-2">Verified</th>
              <th className="p-2">Approved</th>
              <th className="p-2">Blocked</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} className="border-t">
                <td className="p-2">
                  <Link
                    to={`/admin/users/${u._id}`}
                    className="text-blue-700 hover:underline"
                    title="Manage subscription"
                  >
                    {u.name}
                  </Link>
                </td>
                <td className="p-2">{u.email}</td>
                <td className="p-2">{u.phone || "-"}</td>
                <td className="p-2 whitespace-nowrap">
                  {u.subscription ? (
                    <>
                      {u.subscription.planName}
                      {u.subscription.isCustomLimits && (
                        <span className="ml-1 text-xs text-purple-700">(custom)</span>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {u.subscription ? (
                    <>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          SUB_TONE[u.subscription.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {u.subscription.status}
                      </span>
                      {u.subscription.daysRemaining !== null &&
                        u.subscription.daysRemaining <= 14 && (
                          <span className="ml-1 text-xs text-gray-500">
                            · {u.subscription.daysRemaining}d
                          </span>
                        )}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {u.subscription ? (
                    u.subscription.commentLimit === null ? (
                      <span className="text-gray-500">
                        {fmt(u.subscription.commentsUsed)} / ∞
                      </span>
                    ) : (
                      <span className={usageColor(u.subscription.commentPercent)}>
                        {fmt(u.subscription.commentsUsed)} /{" "}
                        {fmt(u.subscription.commentLimit)}
                      </span>
                    )
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {u.activePages ?? 0}
                  {u.subscription?.pageLimit != null && ` / ${u.subscription.pageLimit}`}
                </td>
                <td className="p-2">{u.emailVerified ? "Yes" : "No"}</td>
                <td className="p-2">
                  {u.adminStatus?.approved ? "Yes" : "No"}
                </td>
                <td className="p-2">{u.adminStatus?.blocked ? "Yes" : "No"}</td>
                <td className="p-2 space-x-2">
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() =>
                      updateVerification(u._id, { verify: !u.emailVerified })
                    }
                  >
                    {u.emailVerified ? "Unverify" : "Verify"}
                  </button>
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() => updateVerification(u._id, { reset: true })}
                  >
                    Reset Code
                  </button>
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() =>
                      updateStatus(u._id, {
                        approved: !u.adminStatus?.approved,
                      })
                    }
                  >
                    {u.adminStatus?.approved ? "Unapprove" : "Approve"}
                  </button>
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() =>
                      updateStatus(u._id, { blocked: !u.adminStatus?.blocked })
                    }
                  >
                    {u.adminStatus?.blocked ? "Unblock" : "Suspend"}
                  </button>
                  <button
                    className="px-2 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                    onClick={() => {
                      sessionStorage.setItem("impersonateUserId", u._id);
                      sessionStorage.setItem(
                        "impersonateUserName",
                        u.name || u.email || u._id
                      );
                      window.location.href = "/accounts";
                    }}
                  >
                    View as
                  </button>
                  <button
                    className="px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
                    onClick={() => deleteUser(u._id, u.name || u.email || u._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <button
          className="px-2 py-1 border rounded"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          Prev
        </button>
        <div className="text-xs text-gray-600">
          Page {pagination.page} / {pagination.pages} • {pagination.total} users
        </div>
        <button
          className="px-2 py-1 border rounded"
          onClick={() => setPage((p) => p + 1)}
          disabled={pagination.page >= pagination.pages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminUsers;
